import React from 'react';
import {
  PillarType,
  PillarData,
  TabDefinition,
  PILLAR_ACCENT_COLORS,
} from '../../lib/pillarTypes';
import PillarPageHeader, { type TraySlot } from './PillarPageHeader';
import PillarRelationshipButtons from './PillarRelationshipButtons';
import ConfigurableTabSystem from '../Tabs/ConfigurableTabSystem';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface PillarPageShellProps {
  pillarType: PillarType;
  data: PillarData;
  /** DEV-1396 — optional descriptive tray slots, forwarded to PillarPageHeader.
   *  When absent, the header renders its default project slots unchanged. */
  traySlots?: TraySlot[];
  tabs: TabDefinition[];
  activeTabIndex: number;
  onTabChange: (index: number) => void;
  onPillarNavigate?: (pillarType: PillarType) => void;
  /** Override hero image URL (e.g. from config overrides). Takes priority over data.heroImageUrl. */
  heroImageUrl?: string | null;
  /** Optional controls (buttons, menu, etc.) rendered inside the hero area. */
  heroEditControls?: React.ReactNode;
  /** Optional actions (e.g. Follow) rendered in the hero action zone. */
  headerActions?: React.ReactNode;
  /** Crop overlay rendered at the header level (not inside the edit controls wrapper). */
  heroCropOverlay?: React.ReactNode;
  /** When false, the hero image area collapses with animation. Defaults to true. */
  isHeroExpanded?: boolean;
  /** When true, allows hero menu dropdown to overflow the header. */
  heroMenuOpen?: boolean;
  onBookmark?: () => void;
  onShare?: () => void;
  onEdit?: () => void;
  /** Restore to split-screen (minimize from full-page). */
  onRestore?: () => void;
  onClose?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function PillarPageShell({
  pillarType,
  data,
  traySlots,
  tabs,
  activeTabIndex,
  onTabChange,
  onPillarNavigate,
  heroImageUrl,
  heroEditControls,
  headerActions,
  heroCropOverlay,
  isHeroExpanded = true,
  heroMenuOpen,
  onBookmark,
  onShare,
  onEdit,
  onRestore,
  onClose,
}: PillarPageShellProps) {
  /*
   * Layout / height chain:
   *
   * The shell is a flex COLUMN that fills its parent (height: 100%). The hero is
   * a fixed-height flex item; the tab area is flex:1 and owns the scroll. This
   * preserves the height chain all the way down so tab content that relies on
   * height:100% / flex:1 (e.g. a card grid with `.body { flex:1; overflow:auto }`)
   * actually receives a height. The tab bar sits above the scrolling content and
   * stays put without needing position:sticky.
   */

  const accentColor = PILLAR_ACCENT_COLORS[pillarType];
  const shellStyleWithAccent: React.CSSProperties = {
    ...shellStyle,
    ['--pillar-accent' as any]: accentColor,
  };

  return (
    <div style={shellStyleWithAccent}>
      {/* ── Hero (fixed-height flex item) ── */}
      <div style={heroSlotStyle}>
        <PillarPageHeader
          pillarType={pillarType}
          data={data}
          traySlots={traySlots}
          heroImageUrl={heroImageUrl}
          heroEditControls={heroEditControls}
          headerActions={headerActions}
          heroCropOverlay={heroCropOverlay}
          isHeroExpanded={isHeroExpanded}
          heroMenuOpen={heroMenuOpen}
          onBookmark={onBookmark}
          onShare={onShare}
          onEdit={onEdit}
          onRestore={onRestore}
          onClose={onClose}
        />
      </div>

      {/* ── Tabs + content (flex:1, scrolls internally) ── */}
      <div style={tabAreaStyle}>
        <ConfigurableTabSystem
          tabs={tabs}
          activeTabIndex={activeTabIndex}
          onTabChange={onTabChange}
          accentColor="var(--accent-primary)"
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Inline styles                                                      */
/* ------------------------------------------------------------------ */

/* Flex column that fills its parent — propagates the height chain to the tab
   area so flex:1 / height:100% tab content (card grids) get a real height. */
const shellStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
  overflowX: 'hidden',
};

/* Hero keeps its natural height and never compresses. */
const heroSlotStyle: React.CSSProperties = {
  flex: '0 0 auto',
};

const navRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px 4px',
  flexWrap: 'wrap',
  gap: 8,
  maxWidth: 1080,
  marginInline: 'auto',
};

const tabAreaStyle: React.CSSProperties = {
  flex: '1 1 0',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: 1620,
  marginInline: 'auto',
};

